<template>
  <b-card>
    <h1 class="h4 mb-1">Doctors</h1>
    <p class="text-muted mb-3">Average NP and NC for the selected dates and facilities.</p>
    <p v-if="loading" class="text-muted mb-0">Loading chart...</p>
    <p v-else-if="error" class="text-danger mb-0">{{ error }}</p>
    <p v-else-if="!doctors.length" class="text-muted mb-0">Search a date range to load the chart.</p>
    <vue-apex-charts
      v-else
      type="bar"
      height="420"
      :options="chartOptions"
      :series="series"
    />
  </b-card>
</template>

<script>
import { BCard } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import dashboardApi from '@/core/services/api/dashboard'

const STATUS_COLOR = {
  below_threshold: '#E53935',
  between: '#F9A825',
  target_met: '#43A047',
}

function facilityIds(facilities) {
  if (!Array.isArray(facilities)) return []
  return facilities
    .map(item => (item && typeof item === 'object' ? item.id : item))
    .filter(id => id != null && id !== '')
}

function doctorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return ''
  const first = parts[0].charAt(0).toUpperCase()
  const second = parts[1] ? parts[1].charAt(0).toUpperCase() : ''
  return `${first}${second}`
}

function money(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default {
  name: 'DoctorsChart',
  components: { BCard, VueApexCharts },
  props: {
    start: { type: String, default: '' },
    end: { type: String, default: '' },
    facilities: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      error: '',
      doctors: [],
      goals: { threshold: null, target: null },
      channel: null,
      refreshTimer: null,
    }
  },
  computed: {
    series() {
      return [
        {
          name: 'Avg NP+NC',
          data: this.doctors.map(doctor =>
            doctor.value == null ? null : Number(doctor.value)
          ),
        },
      ]
    },
    chartOptions() {
      const doctors = this.doctors
      const goals = this.goals
      const annotations = []
      if (goals.threshold != null) {
        annotations.push({
          y: Number(goals.threshold),
          borderColor: '#E53935',
          label: {
            text: `Threshold $${money(goals.threshold)}`,
            style: { color: '#fff', background: '#E53935' },
          },
        })
      }
      if (goals.target != null) {
        annotations.push({
          y: Number(goals.target),
          borderColor: '#43A047',
          label: {
            text: `Target $${money(goals.target)}`,
            style: { color: '#fff', background: '#43A047' },
          },
        })
      }
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { distributed: true, borderRadius: 4 } },
        colors: doctors.map(doctor => STATUS_COLOR[doctor.goal_status] || '#B0BEC5'),
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: doctors.map(doctor =>
            doctorInitials(doctor.doctor_name) || doctor.doctor_id
          ),
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
            trim: false,
            style: { fontSize: '11px' },
          },
        },
        yaxis: {
          labels: {
            formatter: value => (value == null ? '' : `$${Math.round(value)}`),
          },
        },
        annotations: { yaxis: annotations },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const doctor = doctors[dataPointIndex]
            if (!doctor) return ''
            const partial =
              doctor.value == null
                ? '<div>Sin NP o sin NC</div>'
                : `<div><strong>$${money(doctor.value)}</strong></div>`
            return `<div class="px-2 py-1">
              <div><strong>${doctor.doctor_name || ''}</strong></div>
              ${partial}
              <div>NP $${money(doctor.avg_np)} · ${doctor.np_count || 0} · $30 ${doctor.np_30_count || 0}</div>
              <div>NC $${money(doctor.avg_nc)} · ${doctor.nc_count || 0} · $30 ${doctor.nc_30_count || 0}</div>
              <div>Membership $${money(doctor.avg_mb)} · ${doctor.mb_count || 0}</div>
            </div>`
          },
        },
      }
    },
  },
  watch: {
    start: 'load',
    end: 'load',
    facilities: {
      deep: true,
      handler: 'load',
    },
  },
  mounted() {
    this.subscribe()
    if (this.start && this.end) this.load()
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer)
    this.unsubscribe()
  },
  methods: {
    async load() {
      if (!this.start || !this.end) return
      this.loading = true
      this.error = ''
      try {
        const response = await dashboardApi.getDoctorsChart({
          start_date: this.start,
          end_date: this.end,
          facilities: facilityIds(this.facilities),
          doctors: [],
        })
        const data = response?.data || response || {}
        this.goals = data.goals || { threshold: null, target: null }
        this.doctors = Array.isArray(data.doctors) ? data.doctors : []
      } catch (error) {
        this.doctors = []
        this.error =
          error?.response?.data?.message || 'Could not load the doctors chart.'
      } finally {
        this.loading = false
      }
    },
    subscribe() {
      if (!this.$pusher || this.channel) return
      this.channel = this.$pusher.subscribe('dashboard.doctors')
      this.channel.bind('doctorStatsUpdated', this.onDoctorStats)
    },
    unsubscribe() {
      if (this.channel) {
        this.channel.unbind('doctorStatsUpdated', this.onDoctorStats)
        this.channel = null
      }
      if (this.$pusher) this.$pusher.unsubscribe('dashboard.doctors')
    },
    onDoctorStats(payload) {
      const facilityId = payload && payload.facility_id
      const selected = facilityIds(this.facilities).map(id => Number(id))
      const all = selected.length === 0
      if (!all && !selected.includes(Number(facilityId))) return
      clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => this.load(), 4000)
    },
  },
}
</script>
